import { TournamentCategoryAdmin } from 'modules/arena/tournaments/types';
import { REGISTRATION_STATUS_LABELS } from 'modules/arena/tournaments/content';
import { Badge } from 'shared/components/ui/Badge';
import { Button } from 'shared/components/ui/Button';
import { ProgressBar } from 'shared/components/ui/ProgressBar';
import { formatBRL } from 'shared/utils/format';

interface CategoryAdminCardProps {
  category: TournamentCategoryAdmin;
  onConfirmRegistration: (categoryId: string, registrationId: string) => void;
  onCancelRegistration: (categoryId: string, registrationId: string) => void;
  onPromoteFromWaitlist: (categoryId: string, waitlistId: string) => void;
}

const REGISTRATION_TONE = {
  confirmada: 'success',
  aguardando_pagamento: 'warning',
  cancelada: 'danger',
} as const;

export function CategoryAdminCard({
  category,
  onConfirmRegistration,
  onCancelRegistration,
  onPromoteFromWaitlist,
}: CategoryAdminCardProps) {
  const activeRegistrations = category.registrations.filter((r) => r.status !== 'cancelada');
  const hasFreeSlot = activeRegistrations.length < category.capacity;

  return (
    <div className="rounded-xl border border-border/40 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">{category.name}</p>
        <span className="text-xs text-ink-secondary">
          {activeRegistrations.length}/{category.capacity} duplas
        </span>
      </div>

      <div className="mt-2">
        <ProgressBar value={activeRegistrations.length} max={category.capacity} />
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {category.registrations.map((registration) => (
          <div key={registration.id} className="flex items-center justify-between gap-2 text-sm">
            <div>
              <p className="text-ink">{registration.participants.join(' & ')}</p>
              <p className="text-xs text-ink-secondary">
                {formatBRL(registration.amountPaid)} de {formatBRL(registration.amountDue)} pagos
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <Badge tone={REGISTRATION_TONE[registration.status]} className="whitespace-nowrap">
                {REGISTRATION_STATUS_LABELS[registration.status]}
              </Badge>
              {registration.status === 'aguardando_pagamento' && (
                <button
                  type="button"
                  onClick={() => onConfirmRegistration(category.id, registration.id)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Confirmar
                </button>
              )}
              {registration.status !== 'cancelada' && (
                <button
                  type="button"
                  onClick={() => onCancelRegistration(category.id, registration.id)}
                  className="text-xs font-medium text-danger hover:underline"
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {category.waitlist.length > 0 && (
        <div className="mt-3 border-t border-border/40 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
            Lista de espera ({category.waitlist.length})
          </p>
          <div className="mt-2 flex flex-col gap-2">
            {category.waitlist.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between text-sm">
                <div>
                  <p className="text-ink">{entry.participants.join(' & ')}</p>
                  <p className="text-xs text-ink-secondary">na fila desde {entry.waitingSinceLabel}</p>
                </div>
                <Button
                  variant="ghost"
                  className="px-3 py-1 text-xs"
                  disabled={!hasFreeSlot}
                  onClick={() => onPromoteFromWaitlist(category.id, entry.id)}
                >
                  Promover
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
